import { useState, useEffect } from 'react';
import { Layout, PageHeader } from '../components/layout';
import { LLMProviderSettings, PromptSelectorBar, PromptEditor } from '../components/settings';
import { usePromptList, usePromptDetail, usePromptMutations } from '../hooks/usePrompts';
import { useSettingsUIStore } from '../store/settingsUIStore';
import type { Prompt } from '../types';

export function SettingsPage() {
  // Prompt list (lightweight, no content)
  const { prompts, activePrompt } = usePromptList();

  // Prompt mutations
  const {
    createPrompt,
    updatePrompt,
    deletePrompt,
    setActive: setActivePrompt,
    isUpdating: isPromptSaving,
  } = usePromptMutations();

  // Zustand for UI-only state
  const {
    selectedPromptId,
    selectPrompt: selectPromptId,
    setSystemPromptExpanded,
    setUserPromptExpanded,
  } = useSettingsUIStore();

  // Local form state for editing
  const [editedName, setEditedName] = useState('');
  const [editedSystemPrompt, setEditedSystemPrompt] = useState('');
  const [editedUserPrompt, setEditedUserPrompt] = useState('');
  const [isNewPromptMode, setIsNewPromptMode] = useState(false);

  // Fetch full prompt detail when a prompt is selected
  const { prompt: selectedPrompt, isLoading: isPromptDetailLoading } = usePromptDetail(selectedPromptId);

  // Get selected prompt summary from list (for is_active status)
  const selectedPromptSummary = prompts.find((p) => String(p.id) === selectedPromptId) || null;

  // Set page title
  useEffect(() => {
    document.title = 'Settings - Translator';
  }, []);

  // Auto-select active prompt on load (but not when creating new prompt)
  useEffect(() => {
    if (activePrompt && !selectedPromptId && !isNewPromptMode) {
      selectPromptId(String(activePrompt.id));
    }
  }, [activePrompt, selectedPromptId, selectPromptId, isNewPromptMode]);

  // Sync edited fields when selected prompt changes
  useEffect(() => {
    if (selectedPrompt) {
      setEditedName(selectedPrompt.name);
      setEditedSystemPrompt(selectedPrompt.system_prompt);
      setEditedUserPrompt(selectedPrompt.user_prompt);
    }
  }, [selectedPrompt]);

  // Clear edited fields when entering new prompt mode
  useEffect(() => {
    if (isNewPromptMode) {
      setEditedName('');
      setEditedSystemPrompt('');
      setEditedUserPrompt('');
    }
  }, [isNewPromptMode]);

  // Keyboard shortcut for saving (Cmd/Ctrl + S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (selectedPrompt && !isPromptSaving) {
          handleSavePrompt();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPrompt, editedName, editedSystemPrompt, editedUserPrompt, isPromptSaving]);

  // Handlers
  const handleSelectPrompt = (promptId: string) => {
    selectPromptId(promptId);
    setSystemPromptExpanded(false);
    setUserPromptExpanded(false);
    if (isNewPromptMode) setIsNewPromptMode(false);
  };

  const handleNewPrompt = () => {
    selectPromptId(null);
    setIsNewPromptMode(true);
    setEditedName('');
    setEditedSystemPrompt('');
    setEditedUserPrompt('');
  };

  const handleCancelNewPrompt = () => {
    setIsNewPromptMode(false);
    if (activePrompt) selectPromptId(String(activePrompt.id));
  };

  const handleCreatePrompt = async () => {
    if (!editedName.trim()) {
      alert('Please enter a prompt name');
      return;
    }
    try {
      const newPrompt = await createPrompt({
        name: editedName,
        system_prompt: editedSystemPrompt,
        user_prompt: editedUserPrompt,
      });
      setIsNewPromptMode(false);
      if (newPrompt?.id) selectPromptId(String(newPrompt.id));
    } catch (error) {
      console.error('Failed to create prompt:', error);
      alert('Failed to create prompt. Please try again.');
    }
  };

  const handleSavePrompt = async () => {
    if (!selectedPrompt) return;
    const updates: Partial<Prompt> = {};
    if (editedName !== selectedPrompt.name) updates.name = editedName;
    if (editedSystemPrompt !== selectedPrompt.system_prompt) updates.system_prompt = editedSystemPrompt;
    if (editedUserPrompt !== selectedPrompt.user_prompt) updates.user_prompt = editedUserPrompt;
    if (Object.keys(updates).length > 0) {
      updatePrompt(String(selectedPrompt.id), updates);
    }
  };

  const handleDeletePrompt = async () => {
    if (!selectedPrompt) return;
    if (prompts.length === 1) {
      alert('Cannot delete the last prompt. Create a new prompt first.');
      return;
    }
    const message = selectedPromptSummary?.is_active
      ? `"${selectedPrompt.name}" is the active prompt. Are you sure you want to delete it?`
      : `Are you sure you want to delete "${selectedPrompt.name}"?`;
    if (!confirm(message)) return;

    deletePrompt(String(selectedPrompt.id));
    selectPromptId(null);
    setEditedName('');
    setEditedSystemPrompt('');
    setEditedUserPrompt('');
  };

  const handleSetActivePrompt = (promptId: string) => {
    setActivePrompt(promptId);
  };

  const hasUnsavedPromptChanges = selectedPrompt && (
    editedName !== selectedPrompt.name ||
    editedSystemPrompt !== selectedPrompt.system_prompt ||
    editedUserPrompt !== selectedPrompt.user_prompt
  );

  return (
    <Layout>
      <PageHeader title="Settings" subtitle="Configure LLM provider and translation prompts" />

      <LLMProviderSettings />

      {/* Translation Prompts Section */}
      <div className="flex-1 flex flex-col">
        <PromptSelectorBar
          prompts={prompts}
          selectedPromptId={selectedPromptId}
          selectedPromptSummary={selectedPromptSummary}
          isNewPromptMode={isNewPromptMode}
          isPromptSaving={isPromptSaving}
          hasUnsavedChanges={!!hasUnsavedPromptChanges}
          editedNameValid={!!editedName.trim()}
          onSelectPrompt={handleSelectPrompt}
          onNewPrompt={handleNewPrompt}
          onCancelNewPrompt={handleCancelNewPrompt}
          onCreatePrompt={handleCreatePrompt}
          onSavePrompt={handleSavePrompt}
          onDeletePrompt={handleDeletePrompt}
          onSetActivePrompt={handleSetActivePrompt}
        />

        <div className="flex-1 bg-white dark:bg-dark-elevated rounded-b-xl border border-gray-200 dark:border-dark-border p-6 overflow-auto">
          <PromptEditor
            selectedPrompt={selectedPrompt || null}
            isNewPromptMode={isNewPromptMode}
            isLoading={isPromptDetailLoading}
            selectedPromptId={selectedPromptId}
            editedName={editedName}
            editedSystemPrompt={editedSystemPrompt}
            editedUserPrompt={editedUserPrompt}
            onNameChange={setEditedName}
            onSystemPromptChange={setEditedSystemPrompt}
            onUserPromptChange={setEditedUserPrompt}
          />
        </div>
      </div>
    </Layout>
  );
}
